import { useState, useEffect } from 'preact/hooks';
import { api, type InviteLink } from '../services/api';

interface InviteLinksSectionProps {
  className?: string;
}

export function InviteLinksSection({ className = '' }: InviteLinksSectionProps) {
  const [inviteLinks, setInviteLinks] = useState<InviteLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const [newLinkData, setNewLinkData] = useState({
    label: '',
    expires_at: '',
    max_uses: ''
  });

  useEffect(() => {
    loadInviteLinks();
  }, []);

  const loadInviteLinks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getInviteLinks();
      if (response.success) {
        setInviteLinks(response.invites);
      } else {
        setError('Failed to load invite links');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invite links');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLink = async (e: Event) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const data: any = {
        label: newLinkData.label || undefined,
      };

      if (newLinkData.expires_at) {
        data.expires_at = newLinkData.expires_at;
      }

      if (newLinkData.max_uses) {
        data.max_uses = parseInt(newLinkData.max_uses);
      }

      const response = await api.createInviteLink(data);
      if (response.success) {
        await loadInviteLinks();
        setNewLinkData({ label: '', expires_at: '', max_uses: '' });
        setShowCreateForm(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create invite link');
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async (token: string, linkId: string) => {
    const fullUrl = `${getParentAppUrl()}/register/${token}`;
    
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (expiresAt: string | undefined) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getParentAppUrl = () => {
    return import.meta.env.VITE_PARENT_APP_URL || 'https://we-shephard-parent-app.vercel.app';
  };

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="flex items-start justify-between mb-4 gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Family Invite Links</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex-shrink-0 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white px-3 py-2 rounded-md text-sm font-medium touch-manipulation"
        >
          + New Link
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Loading invite links...</span>
        </div>
      ) : (
        <>
          {inviteLinks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No invite links created yet</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
              >
                Create your first invite link
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {inviteLinks.map((link) => (
                <div
                  key={link.id}
                  className={`border rounded-lg p-4 ${
                    !link.is_active || isExpired(link.expires_at)
                      ? 'bg-gray-50 border-gray-200'
                      : 'border-gray-200'
                  }`}
                >
                  {/* Label + badge row */}
                  <div className="flex items-center flex-wrap gap-2 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {link.label || 'General Invite Link'}
                    </h3>
                    <span
                      className={`inline-flex flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${
                        !link.is_active
                          ? 'bg-gray-100 text-gray-800'
                          : isExpired(link.expires_at)
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {!link.is_active
                        ? 'Inactive'
                        : isExpired(link.expires_at)
                        ? 'Expired'
                        : 'Active'}
                    </span>
                  </div>

                  {/* Meta info */}
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                    <p>Created: {formatDate(link.created_at)}</p>
                    <p>Uses: {link.use_count}{link.max_uses ? ` / ${link.max_uses}` : ' (unlimited)'}</p>
                    {link.expires_at && (
                      <p className="col-span-2">Expires: {formatDate(link.expires_at)}</p>
                    )}
                  </div>

                  {/* URL + copy button */}
                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 text-xs text-gray-600 font-mono bg-gray-100 px-2 py-2 rounded break-all leading-relaxed">
                      {getParentAppUrl()}/register/{link.token}
                    </div>
                    <button
                      onClick={() => copyToClipboard(link.token, link.id)}
                      className={`flex-shrink-0 w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-md transition-colors touch-manipulation ${
                        copiedLinkId === link.id
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {copiedLinkId === link.id ? '✓ Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Link Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Create New Invite Link</h3>
            </div>

            <form onSubmit={handleCreateLink} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label (Optional)
                </label>
                <input
                  type="text"
                  value={newLinkData.label}
                  onChange={(e) => setNewLinkData({ ...newLinkData, label: (e.target as HTMLInputElement).value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Sunday Service Registration"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date (Optional)
                </label>
                <input
                  type="date"
                  value={newLinkData.expires_at}
                  onChange={(e) => setNewLinkData({ ...newLinkData, expires_at: (e.target as HTMLInputElement).value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Uses (Optional)
                </label>
                <input
                  type="number"
                  value={newLinkData.max_uses}
                  onChange={(e) => setNewLinkData({ ...newLinkData, max_uses: (e.target as HTMLInputElement).value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Leave blank for unlimited"
                  min="1"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}